import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';

import Icon from './Icon';
import NotificationBell from './NotificationBell';
import { C, FONT, SHADOW, APPBAR_HEIGHT } from './tokens';

import CrFlag from 'images/flags/flag-cr.svg';
import GtFlag from 'images/flags/flag-gt.svg';

const REGIONS = {
  cr: { name: 'Costa Rica', code: 'CR', path: '/cr', flag: CrFlag },
  gt: { name: 'Guatemala', code: 'GT', path: '/gt', flag: GtFlag },
};

const Flag = ({ region, w, h }) => (
  <Box component="img" src={REGIONS[region].flag} alt=""
    sx={{ width: w, height: h, borderRadius: '2px', objectFit: 'cover', flex: 'none', display: 'block' }} />
);

// The keydown handler (AppShell) accepts both Cmd and Ctrl; the chip just shows
// whichever the visiting user's platform actually uses.
const IS_MAC = typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
const SHORTCUT_LABEL = IS_MAC ? '⌘K' : 'Ctrl K';

const crumbLinkSx = {
  whiteSpace: 'nowrap', textDecoration: 'none', color: 'inherit', cursor: 'pointer',
  '&:hover': { color: C.turquoiseText, textDecoration: 'underline' },
  '&:focus-visible': { outline: `2px solid ${C.turquoise}`, outlineOffset: 2, borderRadius: '3px' },
};

const crumbCurrentSx = { color: C.text, fontWeight: 700, whiteSpace: 'nowrap' };

const iconBtnSx = {
  width: 30, height: 30, borderRadius: '7px', display: 'grid', placeItems: 'center',
  color: C.textMuted, cursor: 'pointer', background: 'none', border: 0, flex: 'none',
  '&:hover': { bgcolor: C.borderSoft, color: C.text },
  '&:focus-visible': { outline: `2px solid ${C.turquoise}`, outlineOffset: 2 },
};

const TopBar = ({
  expanded, isMobile, railWidth, active, pageCrumbs, onToggleRail, onOpenMobile, onOpenPalette,
  currentRegion, replaceRegionInUrl, onPickRegion, notifications,
}) => {
  const [regionEl, setRegionEl] = useState(null);
  const regionOpen = Boolean(regionEl);
  const region = REGIONS[currentRegion] || REGIONS.cr;

  return (
    <Box
      component="header"
      sx={{
        height: APPBAR_HEIGHT, flex: 'none', bgcolor: '#fff',
        borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center',
        gap: '14px', px: '20px', position: 'fixed', top: 0, right: 0, left: railWidth,
        transition: 'left 200ms cubic-bezier(.2,0,0,1)', zIndex: (t) => t.zIndex.drawer - 1,
      }}
    >
      {/* Mobile hamburger / collapsed expand toggle */}
      {isMobile ? (
        <Box component="button" onClick={onOpenMobile} aria-label="Abrir navegación" sx={iconBtnSx}>
          <Icon name="menu" size={19} />
        </Box>
      ) : (!expanded && (
        <Tooltip title="Expandir el rail" placement="bottom" arrow>
          <Box component="button" onClick={onToggleRail} aria-label="Expandir el rail" sx={iconBtnSx}>
            <Icon name="panel-left-open" size={19} />
          </Box>
        </Tooltip>
      ))}

      {/* Breadcrumb. Prefer the page's own server-provided trail (with real URLs
          back to its listing); fall back to the nav-derived group › module crumb. */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0,
        font: `400 12px/1 ${FONT}`, color: C.textMuted,
      }}>
        {pageCrumbs && pageCrumbs.length ? (
          (isMobile ? pageCrumbs.slice(-1) : pageCrumbs).map((crumb, i, arr) => {
            const isCurrent = i === arr.length - 1; // last level = current page, never a link
            return (
              <React.Fragment key={`${crumb.label}-${i}`}>
                {i > 0 && <Icon name="chevron-right" size={13} color={C.textMuted} />}
                {crumb.url && !isCurrent ? (
                  <Box component="a" href={crumb.url} sx={crumbLinkSx}>{crumb.label}</Box>
                ) : (
                  <Box component="span" sx={crumbCurrentSx}>{crumb.label}</Box>
                )}
              </React.Fragment>
            );
          })
        ) : (
          <>
            {!isMobile && active.groupName && active.groupName !== active.leafName && (
              <>
                {active.groupUrl ? (
                  <Box component="a" href={active.groupUrl} sx={crumbLinkSx}>{active.groupName}</Box>
                ) : (
                  <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{active.groupName}</Box>
                )}
                <Icon name="chevron-right" size={13} color={C.textMuted} />
              </>
            )}
            {/* The module leaf is the current page here → plain text, not a link. */}
            <Box component="span" sx={crumbCurrentSx}>{active.leafName || 'Hub'}</Box>
          </>
        )}
      </Box>

      {/* ⌘K search */}
      {isMobile ? (
        <Box component="button" onClick={onOpenPalette} aria-label="Buscar módulo"
          sx={{ ...iconBtnSx, ml: 'auto' }}>
          <Icon name="search" size={18} />
        </Box>
      ) : (
        <Box
          component="button"
          onClick={onOpenPalette}
          aria-label="Buscar módulo"
          sx={{
            ml: 'auto', width: 300, height: 34, border: `1px solid ${C.border}`, borderRadius: '8px',
            display: 'flex', alignItems: 'center', gap: '8px', px: '12px', color: C.textFaint,
            cursor: 'pointer', bgcolor: '#fff',
            '&:hover': { borderColor: C.turquoise, boxShadow: SHADOW.focusRing },
            '&:focus-visible': { outline: 'none', borderColor: C.turquoise, boxShadow: SHADOW.focusRing },
          }}
        >
          <Icon name="search" size={15} color={C.textFaint} />
          <Box component="span" sx={{ font: `400 12px/1 ${FONT}`, flex: 1, textAlign: 'left' }}>
            Buscar módulo
          </Box>
          <Box component="span" sx={{
            font: `700 10px/1 ${FONT}`, border: `1px solid ${C.border}`, borderRadius: '4px',
            p: '4px 5px', whiteSpace: 'nowrap',
          }}>{SHORTCUT_LABEL}</Box>
        </Box>
      )}

      {/* Region selector */}
      <Box
        component="button"
        onClick={(e) => setRegionEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={regionOpen}
        aria-label={`Región: ${region.name}. Cambiar contexto de datos`}
        sx={{
          display: 'flex', alignItems: 'center', gap: '8px', height: 34, p: '0 11px 0 9px',
          borderRadius: '8px', cursor: 'pointer', bgcolor: '#fff', color: C.text,
          border: `1px solid ${regionOpen ? C.turquoise : C.border}`,
          boxShadow: regionOpen ? SHADOW.focusRingStrong : 'none',
          '&:focus-visible': { outline: 'none', borderColor: C.turquoise, boxShadow: SHADOW.focusRingStrong },
        }}
      >
        <Flag region={currentRegion} w={20} h={14} />
        <Box component="span" sx={{ font: `700 12px/1 ${FONT}`, whiteSpace: 'nowrap' }}>
          {isMobile ? region.code : region.name}
        </Box>
        <Icon name={regionOpen ? 'chevron-up' : 'chevron-down'} size={14} color={C.textMuted} />
      </Box>

      {/* Hidden without a tenant: there is no Admin API token then, and every poll would answer 401 */}
      {notifications.canUseAdminApi && (
        <NotificationBell initialUnreadCount={notifications.unreadCount} iconButtonSx={iconBtnSx} />
      )}

      <Popover
        open={regionOpen}
        anchorEl={regionEl}
        onClose={() => setRegionEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: '10px', width: 296, bgcolor: '#fff', border: `1px solid ${C.border}`,
            borderRadius: '10px', boxShadow: SHADOW.popover, p: '7px',
            display: 'flex', flexDirection: 'column', gap: '2px',
          },
        }}
      >
        <Box sx={{
          font: `700 10px/1 ${FONT}`, letterSpacing: '.12em', color: '#8a919e', p: '8px 10px 6px',
        }}>CONTEXTO DE DATOS</Box>
        {Object.keys(REGIONS).map((key) => {
          const r = REGIONS[key];
          const current = key === currentRegion;
          return (
            <Box
              key={key}
              component="a"
              href={replaceRegionInUrl(key)}
              onClick={() => onPickRegion(key)}
              sx={{
                display: 'flex', alignItems: 'center', gap: '10px', p: '10px', borderRadius: '8px',
                cursor: 'pointer', textDecoration: 'none', color: C.text,
                bgcolor: current ? C.turquoiseTint : 'transparent',
                '&:hover': { bgcolor: current ? C.turquoiseTint : C.borderSoft },
              }}
            >
              <Flag region={key} w={24} h={17} />
              <Box component="span" sx={{ font: `${current ? 700 : 400} 12.5px/1 ${FONT}`, flex: 1 }}>
                {r.name}
              </Box>
              <Box component="span" sx={{ font: `400 10.5px/1 ${FONT}`, color: C.textMuted }}>{r.path}</Box>
              {current && <Icon name="check" size={15} color={C.turquoiseCheck} />}
            </Box>
          );
        })}
        <Box sx={{ height: '1px', bgcolor: C.borderSoft, m: '6px 8px' }} />
        <Box sx={{ display: 'flex', gap: '8px', p: '4px 10px 9px', color: C.textMuted }}>
          <Icon name="info" size={14} color={C.textMuted} />
          <Box component="span" sx={{ font: `400 11px/1.5 ${FONT}` }}>
            Te quedás en <strong>{active.leafName || 'esta vista'}</strong>: solo cambia el segmento de la URL.
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default TopBar;
